import React, { ReactElement, useEffect, useState } from 'react';
import {
  Alert, ActivityIndicator, Pressable, KeyboardAvoidingView,
  Platform, ScrollView, StyleSheet, Text, View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Modal from 'react-native-modal';
// import * as ImagePicker from 'expo-image-picker';
import { FontAwesome } from '@expo/vector-icons';
import { useSelector, useDispatch } from 'react-redux';
import { CameraView } from 'expo-camera';
import NfcManager, { NfcTech } from 'react-native-nfc-manager';
import Style from '../../../constants/Style';
import CloseModalIcon from '../../CloseModalIcon';
import CustomTextField from '../../CustomTextField';
import Colors from '../../../constants/Colors';
// import PlaceholderImageIcon from '../PlaceholderImageIcon';
import ScanIcon from '../../ScanIcon';
import addAsset from '../../../requests/Assets/AddAsset';
import uploadAssetImage from '../../../requests/Assets/UploadAssetImage';
import AlertModal from '../../AlertModal';
import { Area, Floor, Space } from '../../Map/SetFloorsModal/FloorItem';
import getTemplateGroups from '../../../requests/Assets/GetTemplateGroups';
import DeleteIcon24 from '../../24pxIcons/DeleteIcon24';
import {
  ADDITIONAL_FIELD_TYPES, ADDITIONAL_FIELD_TYPE_DETAILS, AdditionalTypeDetails,
} from '../AddTemplateModal/components/FieldList/components/FieldItem';
import { logEvent } from '../../LogEvent';
import isMobile from '../../IsMobile';
import {
  MeterLabel, Template, TemplateField, TemplateType, SensorLabel, AssetCustomField,
} from '../../../constants/Types';
import AssetLabelSelector from './components/AssetLabelSelector';
import TemplateDropdown from './components/TemplateDropdown';
import RegionDropdown from './components/RegionDropdown';
import InvalidCodeModal from './components/InvalidCodeModal';
import getAsset from '../../../requests/Assets/GetAsset';
import { QR_CODE_VERIFICATION_FAILURE } from '../../../constants/AlertMessages';
import PressableStyled from '../../PressableStyled';
import Button from '../../Button';
import QrCodeIcon from '../../30pxIcons/QrCodeIcon';
import NfcIcon from '../../30pxIcons/NfcIcon';
import { updateLocations } from '../../../redux/locationStructureSlice';
import GetEntireLocationStructure from '../../GetEntireLocationStructure';
import { Building } from '../../../screens/LocationManagement/components/AddBuildingModal';
import { getDateComponents } from '../../../constants/Utils';

const alphanumericRegex: string = '^[a-zA-Z0-9-_]+$';

// eslint-disable-next-line no-shadow, no-unused-vars
export enum AssetFormTab {
  // eslint-disable-next-line no-unused-vars
  ESSENTIAL = 'ESSENTIAL', ADDITIONAL = 'ADDITIONAL', LOCATION = 'LOCATION',
}

export default function AddAssetModal(props) {
  const templateCustomFields = (template: Template = null) => [...(template ?? props.template)
    ?.customfields ?? []]
    ?.sort((a: TemplateField, b: TemplateField) => a.Name?.toLowerCase()
      .localeCompare(b.Name?.toLowerCase()))
    .map((field: TemplateField, index: number) => ({
      key: index,
      name: field.Name,
      value: '',
      format: field.Format,
    }));

  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>(null);
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>(null);
  const [tabSelected, setTabSelected] = useState<AssetFormTab>(AssetFormTab.ESSENTIAL);
  const [image, setImage] = useState<string>(null);
  const [scanned, setScanned] = useState<boolean>(true);
  const [assetName, setAssetName] = useState<string>('');
  const [labelsInvalid, setLabelsInvalid] = useState<boolean>(false);
  const [nameInvalid, setNameInvalid] = useState<boolean>(false);
  const [idInvalid, setIdInvalid] = useState<boolean>(false);
  const [idInvalidText, setIdInvalidText] = useState('Please enter asset ID');
  const [buildingInvalid, setBuildingInvalid] = useState<boolean>(false);
  const [floorInvalid, setFloorInvalid] = useState<boolean>(false);
  const [areaInvalid, setAreaInvalid] = useState<boolean>(false);
  const [spaceInvalid, setSpaceInvalid] = useState<boolean>(false);
  const [templateInvalid, setTemplateInvalid] = useState(false);
  const [model, setModel] = useState<string>('');
  const [serial, setSerial] = useState<string>('');
  const [manufacturer, setManufacturer] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [selectedFloor, setSelectedFloor] = useState<Floor>(null);
  const [selectedArea, setSelectedArea] = useState<Area>(null);
  const [selectedSpace, setSelectedSpace] = useState<Space>(null);
  const [assetId, setAssetId] = useState<string | null>('');
  const [floorDropdownVisible, setFloorDropdownVisible] = useState<boolean>(false);
  const [areaDropdownVisible, setAreaDropdownVisible] = useState<boolean>(false);
  const [spaceDropdownVisible, setSpaceDropdownVisible] = useState<boolean>(false);
  const [templateDropdownVisible, setTemplateDropdownVisible] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSyncAlert, setIsSyncAlert] = useState<boolean>(false);
  const [customFields, setCustomFields] = useState<AssetCustomField[]>(templateCustomFields());
  const [templates, setTemplates] = useState<Template[]>([]);
  const [customFieldsValid,
    setCustomFieldsValid] = useState<boolean[]>(customFields?.map(() => true) ?? []);
  const [areasCanHaveAssets, setAreasCanHaveAssets] = useState<boolean>(true);
  const [spacesCanHaveAssets, setSpacesCanHaveAssets] = useState<boolean>(true);
  const [scanAlertMessage, setScanAlertMessage] = useState<string | null>(null);
  const [scannedCodes, setScannedCodes] = useState<string[]>([]);
  const buildingId: string = useSelector((state: { selectedBuilding: { id: string } }) => state
    .selectedBuilding.id);
  const buildings: object = useSelector((state: { locations }) => state.locations.buildings);
  const selectedBuilding: Building | undefined = (buildings && buildingId)
    ? buildings[buildingId] : undefined;
  const [labels, setLabels] = useState<(SensorLabel | MeterLabel)[]>([]);
  const [labelsDropdownShown, setLabelsDropdownShown] = useState<boolean>(false);
  const [nfcPressed, setNfcPressed] = useState<boolean>(false);
  const templateType: TemplateType = selectedTemplate?.type;
  const templateId: string = selectedTemplate?.id;
  const templateName: string = selectedTemplate?.name;
  const dispatch = useDispatch();

  const showAlert = (title: string, message?: string | ReactElement, error: boolean = true) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertVisible(true);
  };

  useEffect(() => {
    getTemplateGroups().then((response) => {
      if (response) {
        setTemplates(response?.groups
          ?.reduce((prev, group) => {
            if (group?.buttonPrototypes?.length) return prev.concat(group.buttonPrototypes);
            return prev;
          }, []) ?? []);
      }
    });
  }, []);

  useEffect(() => {
    setAreasCanHaveAssets(selectedBuilding?.Area?.IsAssetAvailable);
    setSpacesCanHaveAssets(selectedBuilding?.Space?.IsAssetAvailable);
  }, [selectedBuilding]);

  const closeDropdowns: () => void = () => {
    setTemplateDropdownVisible(false);
    setFloorDropdownVisible(false);
    setAreaDropdownVisible(false);
    setSpaceDropdownVisible(false);
    setLabelsDropdownShown(false);
  };

  useEffect(closeDropdowns, [
    tabSelected, selectedBuilding, selectedFloor, selectedArea, selectedSpace]);

  useEffect(() => {
    setFloorInvalid(false);
    setFloorDropdownVisible(false);
  }, [selectedFloor]);

  useEffect(() => {
    setAreaInvalid(false);
    setAreaDropdownVisible(false);
  }, [selectedArea]);

  useEffect(() => {
    setSpaceInvalid(false);
    setSpaceDropdownVisible(false);
  }, [selectedSpace]);

  useEffect(() => setLabelsInvalid(false), [labels]);

  useEffect(() => {
    setSelectedTemplate(props.template);
    setLabels([]);
  }, [props.template]);

  useEffect(() => {
    const newCustomFields = templateCustomFields(selectedTemplate);
    setCustomFields(newCustomFields);
    setCustomFieldsValid(newCustomFields?.map(() => true) ?? []);
    setTemplateDropdownVisible(false);
    setTemplateInvalid(!selectedTemplate);
  }, [selectedTemplate]);

  const handleBarCodeScanned = ({ data }) => {
    if (scannedCodes.includes(data)) return;
    const scanInput = data.split('.tech/')[1]?.split('?')[0];
    if (scanInput) {
      setScanned(true);
      getAsset(scanInput).then((res) => {
        if (!(res && !res.isError)) {
          setAssetId(scanInput);
        } else setScanAlertMessage('already in use');
      }).catch(() => {
        showAlert('Error', QR_CODE_VERIFICATION_FAILURE);
      });
    } else {
      setScanAlertMessage('invalid');
      setScannedCodes([...scannedCodes, data]);
      setScanned(true);
    }
  };

  // const handleImage = (result) => {
  //   if (!result.cancelled) {
  //     const { uri } = result as ImagePicker.ImageInfo;
  //     setImage(uri);
  //   }
  // };

  // const pickImage = async () => {
  //   // No permissions request is necessary for launching the image library
  //   const result = await ImagePicker.launchImageLibraryAsync({
  //     mediaTypes: ImagePicker.MediaTypeOptions.Images,
  //     allowsEditing: true,
  //     quality: 1,
  //   });

  //   handleImage(result);
  // };

  // const takePhoto = async () => {
  //   const result = await ImagePicker.launchCameraAsync({
  //     allowsEditing: true,
  //     quality: 1,
  //   });

  //   handleImage(result);
  // };

  const decodePayload = (payload: number[]): string => {
    const filteredPayload = payload.slice(1);
    return String.fromCharCode(...filteredPayload);
  };

  const restoreDefaults = () => {
    setAssetId('');
    setAssetName('');
    setModel('');
    setSerial('');
    setManufacturer('');
    setDescription('');
    setImage(null);
    setScanned(true);
    closeDropdowns();
    setLoading(false);
    setSelectedTemplate(props.template);
    setTabSelected(AssetFormTab.ESSENTIAL);
    setCustomFields(templateCustomFields());
    setNameInvalid(false);
    setLabelsInvalid(false);
    setIdInvalid(false);
    setBuildingInvalid(false);
    setFloorInvalid(false);
    setAreaInvalid(false);
    setTemplateInvalid(false);
    setSelectedFloor(null);
    setSelectedArea(null);
    setSelectedSpace(null);
  };

  const topSafeAreaInset = useSafeAreaInsets().top;

  const convertDateFields = (fields: AssetCustomField[]) => fields
    ?.map((field: AssetCustomField) => {
      if (field.value?.trim().length > 0 && field.format === ADDITIONAL_FIELD_TYPES.DATE) {
        let date: Date = new Date(field.value.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$2/$1/$3'));
        if (isMobile) {
          const dateComponents: number[] = getDateComponents(field.value);
          date = new Date(dateComponents[2], dateComponents[1] - 1, dateComponents[0]);
        }
        if (date instanceof Date && !Number.isNaN(date.getTime())) {
          return ({ ...field, value: `${date?.toISOString()}` });
        }
        return { ...field, value: field.value?.trim() };
      }
      return { ...field, value: field.value?.trim() };
    }) ?? [];

  const addAssetError = (errorText?: string) => showAlert(
    'Error',
    errorText ?? 'Unable to add asset at the moment. Please try again later',
  );

  const save = () => {
    if (loading) return;
    setLoading(true);
    logEvent('AddAssetModal', 'Save');
    if (image?.trim().length) uploadAssetImage(assetId?.trim(), image?.trim());
    addAsset({
      assetId: assetId?.trim(),
      model: model?.trim(),
      serial: serial?.trim(),
      manufacturer: manufacturer?.trim(),
      description: description?.trim(),
      templateId: templateId?.trim(),
      customFields: convertDateFields(customFields),
      name: assetName?.trim(),
      locationId: selectedSpace?.LocationId?.trim() ?? selectedArea?.LocationId?.trim()
        ?? selectedFloor?.LocationId?.trim() ?? selectedBuilding?.LocationId?.trim(),
      buildingId: selectedBuilding?.LocationId?.trim(),
      labels: labels?.length ? labels : null,
      type: templateType === TemplateType.ASSET ? TemplateType.ASSET : TemplateType.SENSOR,
    }).then((response) => {
      const success = response && !response.isError;
      setLoading(false);
      if (success) {
        showAlert(
          'Success',
          <Text>
            <Text style={Style.alertBoldText}>{assetName}</Text>
            {' was successfully added'}
          </Text>,
          false,
        );
        // Alert.alert('Success', 'Asset was successfully added', [{
        //   text: 'OK',
        //   onPress: () => {
        //     props.setModalVisible(false);
        //   },
        // }]);
      } else addAssetError(response?.errorText);
    }).catch((error) => {
      if (error?.message) addAssetError(error?.message);
      setLoading(false);
    });
  };

  const syncFloors = () => {
    setLoading(true);
    setIsSyncAlert(true);
    GetEntireLocationStructure(
      (locations) => {
        dispatch(updateLocations(locations));
        setLoading(false);
        Alert.alert(
          'Success',
          'Floor data has been successfully synchronized.',
          [{ text: 'OK' }],
        );
        setIsSyncAlert(false);
      },
      () => {
      },
    );
  };

  useEffect(() => {
    if (!alertError && !alertVisible) {
      if (!isSyncAlert) {
        props.setModalVisible(false);
        props.addAsset({
          name: assetName?.trim(),
          id: assetId?.trim(),
          templateId: templateId?.trim(),
          templateName: templateName?.trim(),
          imageUrl: null,
          serial: serial?.trim(),
          model: model?.trim(),
          description: description?.trim(),
          manufacturer: manufacturer?.trim(),
          locationId: selectedSpace?.LocationId?.trim() ?? selectedArea?.LocationId?.trim()
            ?? selectedFloor?.LocationId?.trim() ?? selectedBuilding?.LocationId?.trim(),
          locationBuilding: selectedBuilding?.Name?.trim(),
          locationFloor: selectedFloor?.Name?.trim(),
          locationArea: selectedArea?.Name?.trim(),
          locationSpace: selectedSpace?.Name?.trim(),
          customFields: convertDateFields(customFields),
          label: labels?.length ? labels : null,
          type: templateType === TemplateType.ASSET ? TemplateType.ASSET : TemplateType.SENSOR,
        });
      } else {
        setIsSyncAlert(false);
      }
    }
  }, [alertVisible]);

  const renderSaveContinueButton = () => <Button
    affirmative
    style={styles.saveButton}
    disabled={loading}
    onPress={() => {
      if (tabSelected === AssetFormTab.ESSENTIAL) {
        setTabSelected(AssetFormTab.LOCATION);
        return;
      }
      if (tabSelected === AssetFormTab.LOCATION) {
        setTabSelected(AssetFormTab.ADDITIONAL);
        return;
      }
      closeDropdowns();
      setLabelsDropdownShown(false);
      if (loading) return;
      const errorMessages: string[] = [];
      if (!(assetName?.trim().length > 0)) {
        if (!(errorMessages.length)) setTabSelected(AssetFormTab.ESSENTIAL);
        errorMessages.push('- Asset name cannot be empty');
        setNameInvalid(true);
      }
      if (templateType !== TemplateType.ASSET && !(labels?.length)) {
        if (!(errorMessages?.length)) setTabSelected(AssetFormTab.ESSENTIAL);
        errorMessages.push('- At least one label must be selected');
        setLabelsInvalid(true);
      }
      if (!(assetId?.trim().length > 0)) {
        if (!(errorMessages?.length)) setTabSelected(AssetFormTab.ESSENTIAL);
        errorMessages.push('- Asset ID cannot be empty');
        setIdInvalidText('Please enter asset ID');
        setIdInvalid(true);
      }
      if (assetId?.includes(' ')) {
        if (!(errorMessages?.length)) setTabSelected(AssetFormTab.ESSENTIAL);
        errorMessages.push('- Asset ID cannot have spaces');
        setIdInvalidText('Asset ID cannot have spaces');
        setIdInvalid(true);
      }
      if (!(new RegExp(alphanumericRegex).test(assetId))) {
        if (!(errorMessages?.length)) {
          setTabSelected(AssetFormTab.ESSENTIAL);
        }
        errorMessages.push('- Asset ID must be alphanumeric');
        setIdInvalidText('Asset ID must be alphanumeric');
        setIdInvalid(true);
      }
      if (!templateId) {
        if (!(errorMessages?.length)) setTabSelected(AssetFormTab.ESSENTIAL);
        errorMessages.push('- Template cannot be empty');
        setTemplateInvalid(true);
      }
      if (!selectedBuilding) {
        if (!(errorMessages?.length)) setTabSelected(AssetFormTab.LOCATION);
        errorMessages.push('- A building must be selected');
        setBuildingInvalid(true);
      }
      if (!selectedFloor) {
        if (!(errorMessages?.length)) setTabSelected(AssetFormTab.LOCATION);
        errorMessages.push('- A floor must be selected');
        setFloorInvalid(true);
      }
      if (!areasCanHaveAssets && spacesCanHaveAssets && selectedArea && !selectedSpace) {
        if (!(errorMessages?.length)) setTabSelected(AssetFormTab.LOCATION);
        errorMessages.push('- Areas cannot have assets in this building');
        setAreaInvalid(true);
      }
      if (!spacesCanHaveAssets && selectedSpace) {
        if (!(errorMessages?.length)) setTabSelected(AssetFormTab.LOCATION);
        errorMessages.push('- Spaces cannot have assets in this building');
        setSpaceInvalid(true);
      }
      const updatedCustomFieldsValid: boolean[] = [...customFieldsValid];
      convertDateFields(customFields)?.forEach((field: AssetCustomField, index: number) => {
        if (field.value?.length > 0
          && !new RegExp(field.format ?? ADDITIONAL_FIELD_TYPES.STANDARD)
            .test(field.value)) {
          updatedCustomFieldsValid[index] = false;
          if (!(errorMessages?.length)) setTabSelected(AssetFormTab.ADDITIONAL);
          errorMessages.push(`- Invalid value for ${field.name}: ${field.value}`);
        } else updatedCustomFieldsValid[index] = true;
      });
      setCustomFieldsValid(updatedCustomFieldsValid);
      if (!(errorMessages?.length)) save();
      else showAlert('Error', errorMessages.join('\n'));
    }}
    title={tabSelected === AssetFormTab.ADDITIONAL ? 'Add asset' : 'Continue'}
  />;

  const renderCustomField = (field: AssetCustomField, index: number) => {
    const title: string = field.name.trim();
    const fieldType: AdditionalTypeDetails = ADDITIONAL_FIELD_TYPE_DETAILS
      .filter((type: AdditionalTypeDetails) => type.Format === field.format)[0];
    return (
      <View style={styles.additionalField}>
        <Text style={styles.additionalFieldTitle}>{title}</Text>
        <Text style={styles.additionalFieldType}>
          {fieldType?.description ?? ADDITIONAL_FIELD_TYPE_DETAILS[0].description}
        </Text>
        <CustomTextField
          placeholder={fieldType?.placeholder ?? ADDITIONAL_FIELD_TYPE_DETAILS[0].placeholder}
          value={field.value}
          invalid={!customFieldsValid[index]}
          errorText={fieldType?.invalidMessage ?? ADDITIONAL_FIELD_TYPE_DETAILS[0].invalidMessage}
          updateValue={(text: string) => {
            const isValid: boolean = (fieldType?.Format === ADDITIONAL_FIELD_TYPES.STANDARD
              || fieldType?.Format === ADDITIONAL_FIELD_TYPES.NUMERIC || !fieldType?.Format)
              ? (text.trim().length === 0 || new RegExp(field.format).test(text.trim())) : true;
            setCustomFieldsValid(customFieldsValid.map((value: boolean, valueIndex: number) => (
              valueIndex === index ? isValid : value)));
            setCustomFields(customFields
              .map((customField: AssetCustomField, fieldIndex: number) => (fieldIndex
                === index ? { ...customField, value: text } : customField)));
          }}
          scanner={field.name.toLowerCase().includes('mac') || field.name.toLowerCase().includes('host')}
        />
      </View>
    );
  };

  const scanner = () => {
    if (props.hasPermission === null) return <Text>{'Requesting for camera permission'}</Text>;
    if (props.hasPermission === false) return <Text>{'No access to camera'}</Text>;

    return <CameraView
      onBarcodeScanned={scanned ? undefined : handleBarCodeScanned}
      style={StyleSheet.absoluteFillObject}
    />;
  };

  const renderContent = () => {
    if (!assetId) {
      return (
        <View style={styles.initialContent}>
          <View style={styles.header}>
            <Text style={styles.scanTitle}>
              {'Scan Solve tag'}
            </Text>
            <PressableStyled
              style={{ position: 'absolute', end: 0 }}
              onPress={() => props.setModalVisible(false)}
            >
              <DeleteIcon24 color={Colors.light.hereworksBlack600} />
            </PressableStyled>
          </View>
          <ScanIcon />
          <Text style={styles.scanSubtitle}>
            {'Scan the Solve tag you want to add this asset'}
          </Text>
          <View style={Style.row}>
            <PressableStyled
              style={[styles.scanButton, { marginRight: 6 }]}
              onPress={async () => {
                logEvent('AddAssetModal', 'NFC');

                setNfcPressed(true);
                try {
                  await NfcManager.start();
                  await NfcManager.requestTechnology(NfcTech.Ndef);
                  const tag = await NfcManager.getTag();
                  NfcManager.cancelTechnologyRequest();
                  const decodedTag = decodePayload(tag.ndefMessage[0].payload);
                  setScanned(true);
                  if (decodedTag) handleBarCodeScanned({ data: decodedTag });
                  // handleBarCodeScanned({ data: 'https://dev.hereworks.tech/asset00492?ss=QR' });
                } finally {
                  setNfcPressed(false);
                  NfcManager.cancelTechnologyRequest();
                }
              }}
            >
              <NfcIcon />
              <Text style={styles.scanButtonText}>
                {'Scan NFC tag'}
              </Text>
            </PressableStyled>
            <PressableStyled
              style={[styles.scanButton, { marginLeft: 6 }]}
              onPress={() => {
                logEvent('AddAssetModal', 'Scan_QR');
                setScanned(false);
                // handleBarCodeScanned({ data: 'https://dev.hereworks.tech/asset00492d7?ss=QR' });
              }}
            >
              <QrCodeIcon />
              <Text style={styles.scanButtonText}>
                {'Scan QR code'}
              </Text>
            </PressableStyled>
          </View>
          {nfcPressed ? <Text style={styles.scanSubtitle}>Approach tag</Text> : null}
          {nfcPressed ? <Button style={{ flex: undefined, alignSelf: 'stretch', marginTop: 8 }} title='Cancel NFC Scan' onPress={() => NfcManager.cancelTechnologyRequest()} /> : null}
          <InvalidCodeModal
            message={scanAlertMessage}
            back={() => setScanAlertMessage(null)}
            hide={() => {
              setScanned(true);
              props.setModalVisible(false);
              setScanAlertMessage(null);
            }}
          />
          <Modal
            isVisible={!scanned}
            style={{ alignItems: 'center', margin: 0 }}
            onSwipeComplete={() => setScanned(true)}
            onBackButtonPress={() => setScanned(true)}
            onBackdropPress={() => setScanned(true)}
            swipeDirection={'down'}
            animationIn={'fadeIn'}
            animationOut={'fadeOut'}
          >
            {scanner()}
            <Pressable
              onPress={() => setScanned(true)}
              style={(state: { hovered: boolean, pressed: boolean }) => [
                Style.closeButton, state.hovered && Style.hovered, state.pressed && Style.pressed,
                isMobile && { top: topSafeAreaInset ?? 32 },
              ]}
            >
              <CloseModalIcon />
            </Pressable>
          </Modal>
        </View>
      );
    }

    return <View style={{ alignSelf: 'stretch', flex: 1 }}>
      <Text style={styles.title}>{'Add asset'}</Text>
      <Text style={styles.subTitle}>{'fields marked with * are mandatory'}</Text>
      <View style={styles.tabButtonContainer}>
        {[AssetFormTab.ESSENTIAL, AssetFormTab.LOCATION, AssetFormTab.ADDITIONAL]
          .map((tab: AssetFormTab) => {
            const selected: boolean = tabSelected === tab;
            return <PressableStyled
              key={`tab_option_${tab}`}
              style={[
                styles.smallButton,
                {
                  backgroundColor: selected ? Colors.light.hereworksBlue500 : '#FFF',
                  borderColor: selected
                    ? Colors.light.hereworksBlue500 : Colors.light.hereworksBlue300,
                },
              ]}
              onPress={() => setTabSelected(tab)}
            >
              <Text
                style={[
                  styles.smallButtonText,
                  { color: selected ? '#FFF' : Colors.light.hereworksBlue400 },
                ]}
              >
                {tab}
              </Text>
            </PressableStyled>;
          })}
      </View>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          nestedScrollEnabled
          style={styles.tabContent}
          contentContainerStyle={styles.tabContentContainer}
        >
          {tabSelected === AssetFormTab.ESSENTIAL && (
            <>
              <CustomTextField
                title={'Asset name'}
                required
                invalid={nameInvalid}
                errorText='Please enter asset name'
                value={assetName}
                updateValue={(name) => {
                  setNameInvalid(!(name.toString().trim().length > 0));
                  setAssetName(name);
                }}
              />
              <CustomTextField
                title={'Description'}
                value={description}
                updateValue={setDescription}
              />
              <AssetLabelSelector
                {...{
                  labels, setLabels, templateType, labelsDropdownShown, setLabelsDropdownShown,
                }}
                invalid={labelsInvalid}
              />
              <CustomTextField
                title={'Asset ID (Tag)'}
                required
                errorText={idInvalidText}
                invalid={idInvalid}
                value={assetId}
                editable={!assetId}
                updateValue={(id) => {
                  if (!(id.toString().trim().length > 0)) {
                    setIdInvalid(true);
                    setIdInvalidText('Asset ID cannot be empty');
                  } else if (id.toString().includes(' ')) {
                    setIdInvalid(true);
                    setIdInvalidText('Asset ID cannot have spaces');
                  } else if (!(new RegExp(alphanumericRegex).test(id.toString()))) {
                    setIdInvalid(true);
                    setIdInvalidText('Asset ID must be alphanumeric');
                  } else {
                    setIdInvalid(false);
                  }
                  setAssetId(id);
                }}
              />
              <Text style={styles.heading}>{'Template *'}</Text>
              <Pressable
                style={(state: { hovered: boolean, pressed: boolean }) => [
                  styles.dropdownBox, templateInvalid && styles.dropdownBoxInvalid,
                  state.hovered && Style.hovered, state.pressed && Style.pressed,
                ]}
                onPress={() => setTemplateDropdownVisible(!templateDropdownVisible)}
              >
                <Text style={styles.dropdownBoxText}>{templateName}</Text>
                <View style={{ alignItems: 'flex-end' }}>
                  <FontAwesome
                    size={20}
                    style={{
                      color: templateInvalid ? Colors.light.hereworksRed500
                        : Colors.light.hereworksLightBlue,
                      marginRight: 12,
                    }}
                    name={templateDropdownVisible ? 'angle-up' : 'angle-down'}
                  />
                </View>
              </Pressable>
              {templateInvalid
                && <Text style={styles.errorText}>{'Please select a template'}</Text>}
              {templateDropdownVisible
                && <TemplateDropdown {...{ templates, setSelectedTemplate }} />}
              <CustomTextField
                title={'Model'}
                value={model}
                updateValue={setModel}
              />
              <CustomTextField
                title={'Serial number'}
                value={serial}
                updateValue={setSerial}
                scanner
              />
              <CustomTextField
                title={'Manufacturer'}
                value={manufacturer}
                updateValue={setManufacturer}
              />
            </>
          )}
          {tabSelected === AssetFormTab.LOCATION && <>
            <Text style={styles.heading}>{'Building *'}</Text>
            <View style={[styles.dropdownBox, buildingInvalid && styles.dropdownBoxInvalid]}>
              <Text style={styles.dropdownBoxText}>{selectedBuilding?.Name}</Text>
            </View>
            {buildingInvalid
              && <Text style={styles.errorText}>{'Please select a building'}</Text>}
            {selectedBuilding && <>
              <View style={Style.row}>
                <Text style={styles.heading}>{'Floor *'}</Text>
                <Pressable
                  onPress={syncFloors}
                  style={(state: { hovered: boolean, pressed: boolean }) => [
                    { marginTop: styles.heading.marginTop },
                    state.hovered && Style.hovered, state.pressed && Style.pressed,
                  ]}
                >
                  <Text style={[styles.syncButtonText, loading && styles.syncButtonTextDisabled]}>
                    {loading ? 'Syncing...' : 'Sync Floors'}
                  </Text>
                </Pressable>
              </View>
              <Pressable
                style={(state: { hovered: boolean, pressed: boolean }) => [
                  styles.dropdownBox, floorInvalid && styles.dropdownBoxInvalid,
                  state.hovered && Style.hovered, state.pressed && Style.pressed,
                ]}
                onPress={() => {
                  setFloorDropdownVisible(!floorDropdownVisible);
                  setAreaDropdownVisible(false);
                  setSpaceDropdownVisible(false);
                }}
              >
                <Text style={styles.dropdownBoxText}>
                  {selectedFloor?.Name ?? 'Select a floor'}
                </Text>
                <View style={{ alignItems: 'flex-end' }}>
                  <FontAwesome
                    size={20}
                    style={{
                      color: buildingInvalid ? Colors.light.hereworksRed500
                        : Colors.light.hereworksLightBlue,
                      marginRight: 12,
                    }}
                    name={floorDropdownVisible ? 'angle-up' : 'angle-down'}
                  />
                </View>
              </Pressable>
              {floorInvalid && <Text style={styles.errorText}>{'Please select a floor'}</Text>}
              {floorDropdownVisible && <RegionDropdown
                regions={Object.values(selectedBuilding?.floors ?? {})
                  .sort((prev: Floor, next: Floor) => prev.Index - next.Index)}
                selectedRegion={selectedFloor}
                setSelectedRegion={(floor: Floor) => {
                  setSelectedFloor(floor);
                  setSelectedArea(null);
                  setSelectedSpace(null);
                }}
              />}
            </>}
            {(areasCanHaveAssets || spacesCanHaveAssets)
              && Object.values(selectedFloor?.areas ?? {}).length > 0
              && <View style={{ marginStart: 16 }}>
                <View style={{ flexDirection: 'row' }}>
                  <View style={styles.connectorContainer}>
                    <View style={styles.floorAreaConnector} />
                    <View style={styles.connectorSpot} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <View style={Style.row}>
                      <Text style={styles.heading}>{'Area'}</Text>
                      {selectedArea && <Pressable
                        onPress={() => setSelectedArea(null)}
                        style={(state: { hovered: boolean, pressed: boolean }) => [
                          { marginTop: styles.heading.marginTop },
                          state.hovered && Style.hovered, state.pressed && Style.pressed,
                        ]}
                      >
                        <DeleteIcon24 />
                      </Pressable>}
                    </View>
                    <Pressable
                      style={(state: { hovered: boolean, pressed: boolean }) => [
                        styles.dropdownBox, areaInvalid && styles.dropdownBoxInvalid,
                        state.hovered && Style.hovered, state.pressed && Style.pressed,
                      ]}
                      onPress={() => {
                        setAreaDropdownVisible(!areaDropdownVisible);
                        setFloorDropdownVisible(false);
                        setSpaceDropdownVisible(false);
                      }}
                    >
                      <Text style={styles.dropdownBoxText}>
                        {selectedArea?.Name ?? 'Select an area'}
                      </Text>
                      <View style={{ alignItems: 'flex-end' }}>
                        <FontAwesome
                          size={20}
                          style={{
                            color: areaInvalid ? Colors.light.hereworksRed500
                              : Colors.light.hereworksLightBlue,
                            marginRight: 12,
                          }}
                          name={areaDropdownVisible ? 'angle-up' : 'angle-down'}
                        />
                      </View>
                    </Pressable>
                  </View>
                </View>
                <View style={{ marginStart: 20 }}>
                  {areaInvalid && <Text style={styles.errorText}>{'Please select an area'}</Text>}
                  {areaDropdownVisible && <RegionDropdown
                    regions={Object.values(selectedFloor?.areas ?? {})}
                    selectedRegion={selectedArea}
                    setSelectedRegion={(area: Area) => {
                      setSelectedArea(area);
                      setSelectedSpace(null);
                    }}
                  />}
                </View>
              </View>}
            {spacesCanHaveAssets && Object.values(selectedArea?.spaces ?? {}).length > 0
              && <View style={{ marginStart: 44 }}>
                <View style={{ flexDirection: 'row' }}>
                  <View style={styles.connectorContainer}>
                    <View style={styles.floorAreaConnector} />
                    <View style={styles.connectorSpot} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <View style={Style.row}>
                      <Text style={styles.heading}>{'Space'}</Text>
                      {selectedSpace && <Pressable
                        onPress={() => setSelectedSpace(null)}
                        style={(state: { hovered: boolean, pressed: boolean }) => [
                          { marginTop: styles.heading.marginTop },
                          state.hovered && Style.hovered, state.pressed && Style.pressed,
                        ]}
                      >
                        <DeleteIcon24 />
                      </Pressable>}
                    </View>
                    <Pressable
                      style={(state: { hovered: boolean, pressed: boolean }) => [
                        styles.dropdownBox, areaInvalid && styles.dropdownBoxInvalid,
                        state.hovered && Style.hovered, state.pressed && Style.pressed,
                      ]}
                      onPress={() => {
                        setSpaceDropdownVisible(!spaceDropdownVisible);
                        setFloorDropdownVisible(false);
                        setAreaDropdownVisible(false);
                      }}
                    >
                      <Text style={styles.dropdownBoxText}>
                        {selectedSpace?.Name ?? 'Select a space'}
                      </Text>
                      <View style={{ alignItems: 'flex-end' }}>
                        <FontAwesome
                          size={20}
                          style={{
                            color: spaceInvalid ? Colors.light.hereworksRed500
                              : Colors.light.hereworksLightBlue,
                            marginRight: 12,
                          }}
                          name={spaceDropdownVisible ? 'angle-up' : 'angle-down'}
                        />
                      </View>
                    </Pressable>
                  </View>
                </View>
                <View style={{ marginStart: 20 }}>
                  {spaceInvalid
                    && <Text style={styles.errorText}>{'Please select a space'}</Text>}
                  {spaceDropdownVisible && <RegionDropdown
                    regions={Object.values(selectedArea?.spaces ?? {})}
                    selectedRegion={selectedSpace}
                    setSelectedRegion={(setSelectedSpace)}
                  />}
                </View>
              </View>}
          </>}
          {tabSelected === AssetFormTab.ADDITIONAL && <>
            {customFields?.filter((field: AssetCustomField) => !field?.name.toLowerCase().includes('geo'))
              ?.map((field, index) => (<View key={index}>
                {renderCustomField(field, index)}
              </View>))}
            {!(customFields?.filter((field: AssetCustomField) => !field?.name.toLowerCase().includes('geo'))
              .length > 0) && <View style={styles.noFieldsTextContainer}>
                <Text style={styles.noFieldsText}>{'No additional fields'}</Text>
              </View>}
          </>}
        </ScrollView>
        {renderSaveContinueButton()}
      </KeyboardAvoidingView>
      {isMobile && <PressableStyled
        onPress={() => {
          if (tabSelected === AssetFormTab.ADDITIONAL) {
            setTabSelected(AssetFormTab.LOCATION);
            return;
          }
          if (tabSelected === AssetFormTab.LOCATION) {
            setTabSelected(AssetFormTab.ESSENTIAL);
            return;
          }
          logEvent('EditAssetModal', 'Close_Modal');
          props.setModalVisible(false);
        }}
        style={styles.backButton}
      >
        <FontAwesome
          size={24}
          name='angle-left'
          color={Colors.light.hereworksBlack600}
        />
      </PressableStyled>}
      <PressableStyled
        onPress={() => {
          logEvent('AddAssetModal', 'Close_Modal');
          props.setModalVisible(false);
        }}
        style={Style.closeButtonBlack}
      >
        <DeleteIcon24 color={Colors.light.hereworksBlack600} />
      </PressableStyled>
    </View>;
  };

  const insets = useSafeAreaInsets();

  return (
    <Modal
      isVisible={props.visible}
      animationIn='slideInUp'
      animationOut='slideOutDown'
      onBackButtonPress={() => props.setModalVisible(false)}
      onBackdropPress={() => props.setModalVisible(false)}
      onSwipeComplete={() => props.setModalVisible(false)}
      style={[Style.modal, styles.modal, Platform.OS === 'ios' && { marginTop: insets.top }]}
      onModalHide={restoreDefaults}
    >
      <View
        style={[
          styles.container,
          assetId ? styles.formContainer : styles.scanContainer,
          { marginTop: 7 },
        ]}>
        {renderContent()}
      </View>
      <AlertModal
        modalVisible={alertVisible}
        error={alertError}
        title={alertTitle}
        message={alertMessage}
        setModalVisible={setAlertVisible}
        action={() => setAlertVisible(false)}
      />
      <ActivityIndicator
        size="large"
        animating={loading}
        style={Style.activityIndicator}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  modal: {
    justifyContent: 'flex-end',
  },
  container: {
    width: '100%',
    backgroundColor: '#FFF',
    paddingBottom: 32,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    paddingHorizontal: 12,
  },
  scanContainer: {
    alignSelf: 'flex-end',
  },
  formContainer: {
    flex: 1,
  },
  title: {
    marginTop: 16,
    fontSize: 22,
    lineHeight: 33,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    marginVertical: 10,
    textAlign: 'center',
  },
  subTitle: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack300,
    textAlign: 'center',
  },
  smallButtonText: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    marginHorizontal: 12,
    color: '#fff',
  },
  smallButton: {
    borderRadius: 4,
    marginHorizontal: 4,
    height: 28,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    backgroundColor: Colors.light.hereworksBlue500,
    alignSelf: 'center',
    justifyContent: 'center',
  },
  heading: {
    fontSize: 16,
    lineHeight: 24,
    marginTop: 12,
    marginBottom: 4,
    alignSelf: 'flex-start',
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  dropdownBox: {
    width: '100%',
    height: 44,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 10,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    zIndex: 2,
  },
  dropdownBoxInvalid: {
    borderColor: Colors.light.hereworksRed500,
  },
  dropdownBoxText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    marginVertical: 9.5,
    marginLeft: 12,
    flex: 1,
  },
  errorText: {
    color: Colors.light.hereworksRed500,
    fontFamily: 'Roboto_400Regular',
    marginTop: 4,
    fontSize: 12,
    lineHeight: 20,
    alignSelf: 'flex-start',
  },
  tabContentContainer: {
    paddingBottom: 16,
    paddingTop: 6,
    paddingHorizontal: 12,
    minHeight: '80%',
  },
  tabContent: {
    alignSelf: 'stretch',
    flex: 1,
    paddingHorizontal: 8,
    marginHorizontal: -8,
  },
  tabButtonContainer: {
    flexDirection: 'row',
    marginTop: 32,
    marginBottom: 12,
    alignSelf: 'center',
  },
  initialContent: {
    alignItems: 'center',
    padding: 16,
    justifyContent: 'center',
    width: '100%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignSelf: 'stretch',
    marginBottom: 40,
  },
  connectorContainer: {
    flexDirection: 'row',
    marginBottom: 17,
    alignSelf: 'stretch',
  },
  floorAreaConnector: {
    width: 16,
    marginBottom: 2,
    alignSelf: 'stretch',
    borderStartWidth: 1,
    borderBottomWidth: 1,
    borderBottomStartRadius: 6,
    borderColor: Colors.light.hereworksBlue100,
  },
  connectorSpot: {
    width: 3,
    height: 6,
    borderTopStartRadius: 3,
    borderBottomStartRadius: 3,
    backgroundColor: Colors.light.hereworksBlue100,
    alignSelf: 'flex-end',
  },
  additionalField: {
    marginBottom: 16,
  },
  additionalFieldTitle: {
    fontSize: 16,
    lineHeight: 24,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Roboto_400Regular',
  },
  additionalFieldType: {
    fontSize: 12,
    lineHeight: 18,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    marginBottom: 8,
  },
  noFieldsTextContainer: {
    padding: 16,
    backgroundColor: Colors.light.hereworksWhite100,
    borderRadius: 10,
    marginBottom: 16,
    alignSelf: 'stretch',
  },
  noFieldsText: {
    fontSize: 18,
    lineHeight: 27,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack400,
  },
  saveButton: {
    flex: undefined,
    alignSelf: 'stretch',
    marginStart: undefined,
    marginHorizontal: 12,
    height: 40,
  },
  templateDropdownContainer: {
    height: 200,
    alignSelf: 'stretch',
  },
  scanTitle: {
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 14,
    marginBottom: 24,
    color: Colors.light.hereworksBlack600,
    textAlign: 'center',
    alignSelf: 'center',
  },
  scanSubtitle: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 16,
    lineHeight: 20,
    marginTop: 20,
    color: Colors.light.hereworksBlack600,
    textAlign: 'center',
  },
  scanButton: {
    backgroundColor: '#fff',
    borderRadius: 4,
    borderWidth: 1,
    marginTop: 12,
    borderColor: Colors.light.hereworksBlue500,
    alignSelf: 'stretch',
    alignItems: 'center',
    paddingVertical: 16,
  },
  scanButtonText: {
    color: Colors.light.hereworksBlue500,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    marginVertical: 8,
    marginHorizontal: 24,
    textAlign: 'center',
  },
  backButton: {
    position: 'absolute',
    start: 8,
    top: 12,
  },
  syncButtonText: {
    color: Colors.light.hereworksBlue500,
    fontSize: 14,
    fontWeight: '500',
  },
  syncButtonTextDisabled: {
    color: Colors.light.hereworksBlack400,
  },
});
